// ============================================================
// 데이터셋 엑셀 행 가져오기 — 순수 변환 층
// ============================================================
//
// 줄기: docs/work-streams/done/dataset-excel-row-import.md (P2)
//
// 서버(P1 `parse-xlsx`)는 원시 격자(문자열 2차원 배열)만 준다. "몇 번째 줄이 헤더인가 /
// 어느 엑셀 칼럼이 어느 데이터셋 칼럼인가 / 셀 문자열을 어떤 값으로 저장할 것인가"는 전부
// 여기서 결정한다. 모달(DatasetExcelImportModal)은 이 함수들의 결과를 그리기만 한다.
//
// 저장 값의 정본은 **rc 렌더러가 읽는 형태**다(admin 기본행 편집기의 text input이 아니라):
//   checkbox → boolean          (composerWidgetTreeRender `checked={Boolean(value)}`)
//   number   → number | ''      (rc가 onChange에서 `Number(...)`로 쓴다)
//   select/radio → option.value (rc `<select value={value}>` / radio `checked={value===opt.value}`)
//   text/textarea → string
// 문자열 'false'가 체크로 새는 함정을 원천 차단하려면 checkbox는 **반드시** boolean이어야 한다.

import type { DataDefField } from '@safetyhub/shared';

/**
 * 한 셀의 변환 실패·의심 기록. 행은 버리지 않고 값만 보정하되, 무엇을 어떻게 바꿨는지
 * 미리보기에서 전부 보여 준다(침묵 손실 금지).
 */
export interface ImportWarning {
  /**
   * 읽어 온 격자 기준 줄 번호(1-based, 헤더 줄 포함).
   *
   * 서버 격자는 시트의 **사용 범위 시작**부터 잘라 오므로, 표 위에 빈 행이 있는 파일에서는
   * 엑셀 화면의 행 번호와 어긋난다. 그래서 화면 문구도 "표에서 N번째 줄"로 적는다.
   */
  rowNumber: number;
  fieldKey: string;
  fieldLabel: string;
  /** 변환 전 엑셀 원문 */
  raw: string;
  message: string;
}

/**
 * 상호배타 열 쌍(예: 병합 「점검결과」 아래 양호·불량)을 한 칼럼의 값 원천으로 삼는 참조.
 *
 * subLabels를 ref 안에 들고 다니는 이유: 값 변환이 하위 라벨을 쓴다(체크박스가 아닌 칼럼에
 * 매핑되면 마크된 쪽의 라벨 문자열이 값이 된다). 쌍 목록을 별도 채널로 넘기면 buildRows가
 * 두 입력의 정합을 다시 검사해야 한다 — ref가 자기완결이면 그 계약이 사라진다.
 */
export interface PairColumnRef {
  /** [첫 열, 둘째 열] 엑셀 index — 첫 열(양호) 마크가 체크(true)다. */
  pair: [number, number];
  /** [첫 열, 둘째 열] 하위 헤더 라벨(예: ['양호', '불량']) */
  subLabels: [string, string];
}

/** 한 데이터셋 칼럼의 값 원천 — 엑셀 열 하나 / 배타 쌍 / 미매핑(null). */
export type ColumnSource = number | PairColumnRef | null;

/** 데이터셋 칼럼 key → 값 원천. null = 미매핑(기본값으로 채움). */
export type ColumnMapping = Record<string, ColumnSource>;

/** 쌍 참조 판별 — mapping 값의 세 갈래 중 객체 갈래만 골라낸다. */
export function isPairRef(source: ColumnSource | undefined): source is PairColumnRef {
  return typeof source === 'object' && source !== null;
}

/** 미매핑 칼럼에 넣을 기본값 — 사용자가 친 원문 문자열. 셀과 같은 변환을 거쳐 저장된다. */
export type ColumnDefaults = Record<string, string>;

// ------------------------------------------------------------
// 헤더 매칭
// ------------------------------------------------------------

/**
 * 헤더 라벨 비교용 정규화 — 공백(전각 포함)·괄호·구두점을 지우고 소문자로.
 *
 * 엑셀 헤더는 같은 뜻이라도 `점검 항목` / `점검항목` / `점검항목(필수)`처럼 갈린다.
 * 여기서 지우는 것은 **비교 키**일 뿐이고 표시 라벨은 원문 그대로 쓴다.
 */
export function normalizeHeaderLabel(raw: string): string {
  // NFKC가 전각 공백(U+3000)·NBSP를 일반 공백으로 접어 주므로 \s만으로 전부 걷힌다.
  return raw
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .replace(/[()[\]{}·・:：,，.。\-_/\\]/g, '')
    .toLowerCase();
}

/**
 * 데이터셋 칼럼 ← 엑셀 칼럼 자동 매칭(라벨 동일 시).
 *
 * 라벨이 비어 있는 칼럼은 key로도 맞춰 본다(＋칼럼으로 갓 만든 `field3` 같은 칼럼).
 * 한 엑셀 칼럼이 두 데이터셋 칼럼에 붙지 않도록 먼저 잡은 쪽이 차지한다(중복 매핑은
 * 미리보기에서 같은 값이 두 칸에 들어가는 혼란만 만든다).
 *
 * `pairs`(감지된 배타 쌍)를 주면 **상위 헤더 라벨을 개별 칼럼보다 먼저** 맞춰 본다 —
 * 데이터셋에 「점검결과」 칼럼이 있으면 합성 항목(양호/불량 합침)이 붙는 게 맞고, 개별 열의
 * 합성 라벨('점검결과 양호')과는 어차피 겹치지 않는다. 쌍이 붙으면 그 두 열은 개별 매칭에서도
 * 제외한다(같은 값이 두 경로로 들어가는 중복 차단).
 */
export function autoMatchColumns(
  fields: readonly Pick<DataDefField, 'key' | 'label'>[],
  headerCells: readonly string[],
  pairs: readonly ExclusivePairCandidate[] = [],
): ColumnMapping {
  const normalizedHeaders = headerCells.map((h) => normalizeHeaderLabel(h));
  const normalizedPairTops = pairs.map((p) => normalizeHeaderLabel(p.topLabel));
  const used = new Set<number>();
  const usedPairs = new Set<number>();
  const mapping: ColumnMapping = {};
  for (const field of fields) {
    const candidates = [field.label, field.key]
      .map((s) => normalizeHeaderLabel(s ?? ''))
      .filter((s) => s.length > 0);
    let matched: ColumnSource = null;
    for (const candidate of candidates) {
      // 쌍의 두 열 중 하나라도 개별 매칭이 이미 차지했으면 그 쌍은 붙이지 않는다 — 같은 열이
      // 개별·합침 두 경로로 두 칼럼에 들어가는 중복은 개별↔개별과 마찬가지로 차단해야 한다.
      const pairIdx = normalizedPairTops.findIndex(
        (top, i) =>
          !usedPairs.has(i) &&
          !used.has(pairs[i].colIndexes[0]) &&
          !used.has(pairs[i].colIndexes[1]) &&
          top.length > 0 &&
          top === candidate,
      );
      if (pairIdx >= 0) {
        const pair = pairs[pairIdx];
        matched = { pair: pair.colIndexes, subLabels: pair.subLabels };
        usedPairs.add(pairIdx);
        used.add(pair.colIndexes[0]);
        used.add(pair.colIndexes[1]);
        break;
      }
      const idx = normalizedHeaders.findIndex(
        (h, i) => !used.has(i) && h.length > 0 && h === candidate,
      );
      if (idx >= 0) {
        matched = idx;
        break;
      }
    }
    if (typeof matched === 'number') used.add(matched);
    mapping[field.key] = matched;
  }
  return mapping;
}

/**
 * 헤더 행 후보 — 채워진 칸이 2개 이상인 **첫 행**.
 *
 * 엑셀 데이터는 제목 줄("2026년 점검표")이 맨 위에 한 칸만 차지하는 경우가 흔해서,
 * "첫 비어 있지 않은 행"을 그대로 쓰면 헤더를 놓친다. 2칸 이상인 행이 없으면 첫 비어 있지
 * 않은 행, 그것도 없으면 0.
 */
export function suggestHeaderRowIndex(cells: readonly string[][]): number {
  let firstNonEmpty = -1;
  for (let r = 0; r < cells.length; r += 1) {
    const filled = cells[r].filter((c) => c.trim().length > 0).length;
    if (filled >= 2) return r;
    if (filled > 0 && firstNonEmpty < 0) firstNonEmpty = r;
  }
  return firstNonEmpty >= 0 ? firstNonEmpty : 0;
}

/** 헤더가 걸칠 수 있는 최대 줄 수 — 모달의 "헤더 줄 수" 선택지 상한. */
export const MAX_HEADER_ROW_COUNT = 3;

/**
 * 헤더 줄 수 제안 — 헤더 시작 줄의 **다음 줄**이 병합 다행 헤더의 아랫줄로 보이면 2, 아니면 1.
 *
 * 서버가 병합 셀을 anchor 텍스트로 복제해 주므로, 2행 헤더는 격자에서 이런 모양이 된다:
 *   `['구분', '점검항목', '점검결과', '점검결과']`   ← 세로 병합은 아래에도 같은 값,
 *   `['구분', '점검항목', '양호',     '불량'    ]`   ← 가로 병합 아래에만 새 문구.
 * 그래서 다음 줄의 채워진 칸이 전부 "위와 같은 값(세로 병합 복제)"이거나 "위가 가로 병합
 * 구간(좌우 이웃과 같은 값) 또는 빈 칸"일 때만 아랫줄 헤더로 본다. 위 칸이 독립 라벨인데
 * 다른 값이 하나라도 있으면 데이터 줄이다 — 확신이 없으면 1(사용자가 select로 올릴 수 있으니
 * 과감히 제안하지 않는다. 3줄 제안도 같은 이유로 하지 않는다).
 */
export function suggestHeaderRowCount(
  cells: readonly string[][],
  headerRowIndex: number,
): number {
  const header = cells[headerRowIndex];
  const next = cells[headerRowIndex + 1];
  if (!header || !next) return 1;

  let subHeaderColumns = 0;
  for (let c = 0; c < next.length; c += 1) {
    const nextText = (next[c] ?? '').trim();
    if (nextText.length === 0) continue;
    const headerText = (header[c] ?? '').trim();
    // 위와 같은 값 — 세로 병합 복제가 이어진 것. 헤더 판단의 근거도 반증도 아니다.
    if (nextText === headerText) continue;
    const leftText = (header[c - 1] ?? '').trim();
    const rightText = (header[c + 1] ?? '').trim();
    const underHorizontalMerge =
      headerText.length > 0 && (headerText === leftText || headerText === rightText);
    if (headerText.length === 0 || underHorizontalMerge) {
      subHeaderColumns += 1;
    } else {
      return 1;
    }
  }
  return subHeaderColumns > 0 ? 2 : 1;
}

/**
 * 여러 헤더 줄을 칼럼별로 합쳐 하나의 라벨을 만든다 — 위→아래 공백 join, 인접 중복 제거.
 *
 * 병합 복제 때문에 위아래가 같은 값이면 한 번만 쓴다("구분"+"구분" → "구분"). 가로 병합
 * 상단이 복제된 칼럼은 상단+하단이 붙는다("점검결과"+"양호" → "점검결과 양호"). 자동
 * 매칭·구성 모드 라벨·미리보기 헤더가 전부 이 합성 라벨을 쓴다(비교는 normalizeHeaderLabel이
 * 공백을 걷어내므로 join 구분자는 표시용일 뿐이다).
 */
export function buildHeaderLabels(
  cells: readonly string[][],
  headerRowIndex: number,
  headerRowCount: number,
): string[] {
  const rows = cells.slice(headerRowIndex, headerRowIndex + Math.max(1, headerRowCount));
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const labels: string[] = [];
  for (let c = 0; c < width; c += 1) {
    const parts: string[] = [];
    for (const row of rows) {
      const text = (row[c] ?? '').trim();
      if (text.length === 0) continue;
      if (parts.length > 0 && parts[parts.length - 1] === text) continue;
      parts.push(text);
    }
    labels.push(parts.join(' '));
  }
  return labels;
}

// ------------------------------------------------------------
// 상호배타 열 쌍 감지 (병합 헤더 아래 양호/불량)
// ------------------------------------------------------------

/** 감지된 상호배타 열 쌍 — 병합 상위 헤더 하나 아래 배타로 표시되는 두 열. */
export interface ExclusivePairCandidate {
  /** [첫 열, 둘째 열] 엑셀 index (인접) */
  colIndexes: [number, number];
  /** 상위 헤더 문구(예: '점검결과') — 병합 칼럼의 표시명이자 자동 매칭 키가 된다. */
  topLabel: string;
  /** [첫 열, 둘째 열] 하위 헤더 라벨(예: ['양호', '불량']) */
  subLabels: [string, string];
}

/**
 * 두 열이 데이터 구간에서 상호배타 표시와 모순되지 않는가 — 같은 행 양쪽 동시 표시가 있으면 false.
 *
 * "표시"는 **비어 있지 않은 셀**이다. O/V는 체크 표기지만 X가 적힌 칸도 "그 칸을 골랐다"는
 * 뜻으로 쓰는 파일이 있어(불량 칸에 X), 표기 사전(parseCheckboxText)으로 참/거짓을 가리면
 * 그런 파일에서 배타 판정이 뒤집힌다. 채움 여부만 보면 두 해석 모두에서 안전하다.
 *
 * 표시가 하나도 없어도 **참**이다 — 점검표는 빈 양식(칸 전부 공란)으로 올리는 경우가 오히려
 * 기본이라, "마크 행 ≥1"을 요구하면 실사용 대부분에서 병합 제안이 죽는다(2026-08-09 실파일).
 * 빈 데이터에서는 헤더 구조(상위 병합 동일+하위 상이)가 유일하고 충분한 근거다.
 */
function columnsAreExclusive(
  cells: readonly string[][],
  dataStartRow: number,
  col1: number,
  col2: number,
): boolean {
  for (let r = dataStartRow; r < cells.length; r += 1) {
    const first = (cells[r]?.[col1] ?? '').trim().length > 0;
    const second = (cells[r]?.[col2] ?? '').trim().length > 0;
    if (first && second) return false;
  }
  return true;
}

/**
 * 병합 상위 헤더 아래 상호배타 두 열을 찾는다 — "점검결과 1칼럼(양호=true)" 병합 후보.
 *
 * 조건 전부 만족 시에만 후보다(제안일 뿐 — 병합 여부는 사용자가 결정한다):
 * - 다행 헤더(2줄 이상). 1줄 헤더에는 상위/하위 구분이 없어 합성 근거가 없다.
 * - 첫 헤더 줄에서 같은 문구가 **정확히 2열** 연속(서버가 가로 병합을 anchor 텍스트로 복제해
 *   준 결과). 3열 이상(상/중/하)은 bool 하나로 접을 수 없어 제외.
 * - 마지막 헤더 줄의 하위 라벨 둘이 모두 있고 서로 다르며 상위 문구와도 다르다(상위와 같으면
 *   세로 병합 복제지 하위 라벨이 아니다).
 * - 데이터 구간에서 두 열이 상호배타(columnsAreExclusive).
 */
export function detectExclusivePairs(
  cells: readonly string[][],
  headerRowIndex: number,
  headerRowCount: number,
): ExclusivePairCandidate[] {
  if (headerRowCount < 2) return [];
  const topRow = cells[headerRowIndex] ?? [];
  const subRow = cells[headerRowIndex + headerRowCount - 1] ?? [];
  const width = Math.max(topRow.length, subRow.length);
  const dataStartRow = headerRowIndex + headerRowCount;

  const pairs: ExclusivePairCandidate[] = [];
  // 첫 헤더 줄을 같은 문구의 연속 구간(run)으로 끊는다 — 길이 2 구간만 쌍 후보.
  let runStart = 0;
  for (let c = 1; c <= width; c += 1) {
    const prevText = (topRow[c - 1] ?? '').trim();
    const currentText = c < width ? (topRow[c] ?? '').trim() : null;
    if (currentText !== null && currentText === prevText) continue;

    const runLength = c - runStart;
    const topLabel = (topRow[runStart] ?? '').trim();
    if (runLength === 2 && topLabel.length > 0) {
      const sub1 = (subRow[runStart] ?? '').trim();
      const sub2 = (subRow[runStart + 1] ?? '').trim();
      const subsAreLabels =
        sub1.length > 0 && sub2.length > 0 && sub1 !== sub2 && sub1 !== topLabel && sub2 !== topLabel;
      if (subsAreLabels && columnsAreExclusive(cells, dataStartRow, runStart, runStart + 1)) {
        pairs.push({
          colIndexes: [runStart, runStart + 1],
          topLabel,
          subLabels: [sub1, sub2],
        });
      }
    }
    runStart = c;
  }
  return pairs;
}

// ------------------------------------------------------------
// 셀 값 변환
// ------------------------------------------------------------

/** 체크로 읽는 표기 — 정규화(공백 제거·소문자) 후 비교. */
const CHECKBOX_TRUE_TEXTS = new Set([
  'o',
  'ｏ',
  '○',
  '●',
  'v',
  '✓',
  '✔',
  'y',
  'yes',
  'true',
  '1',
  '예',
  '유',
  '해당',
  '적용',
]);

/** 해제로 읽는 표기 — 빈 셀도 여기(false)로 취급한다. */
const CHECKBOX_FALSE_TEXTS = new Set([
  'x',
  'ｘ',
  '×',
  '✗',
  '－',
  '-',
  'n',
  'no',
  'false',
  '0',
  '아니오',
  '아니요',
  '무',
  '해당없음',
  '미적용',
]);

/**
 * 체크박스 표기 판정 — true/false/판정불가(null).
 *
 * 빈 문자열은 false(미체크)다. 목록 밖 문자열만 null을 돌려주고, 호출측이 경고와 함께
 * false로 저장한다 — 어떤 경우에도 **문자열을 그대로 저장하지 않는다**(문자열 'false'가
 * `Boolean(value)`에서 체크로 읽히는 함정).
 */
export function parseCheckboxText(raw: string): boolean | null {
  const t = normalizeHeaderLabel(raw);
  if (t.length === 0) return false;
  if (CHECKBOX_TRUE_TEXTS.has(t)) return true;
  if (CHECKBOX_FALSE_TEXTS.has(t)) return false;
  return null;
}

/**
 * 숫자 표기 판정 — 천 단위 쉼표·통화 기호·퍼센트 기호·단위 접미는 걷어내지 않는다.
 *
 * 쉼표만 예외로 제거한다(엑셀 표시 문자열이 `1,234`로 오는 게 흔하고 뜻이 모호하지 않다).
 * 그 밖의 문자가 섞이면 null — 조용히 `parseFloat`로 앞부분만 취하면 `12개`가 12로
 * 둔갑해 원문 확인 기회를 잃는다.
 */
export function parseNumberText(raw: string): number | null {
  const t = raw.trim().replace(/,/g, '');
  if (t.length === 0) return null;
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** 한 셀 변환 결과 — 저장할 값 + (있으면) 경고 문구. */
export interface CellConversion {
  value: unknown;
  warning?: string;
}

/**
 * select/radio 칼럼의 옵션 조회표 — 셀마다 옵션 전체를 다시 정규화하지 않기 위한 것이다.
 *
 * 매칭 순서(엑셀 원문이 option.value와 완전히 같은가 → 정규화한 라벨이 같은가)를 지키려면
 * 두 축을 따로 들고 있어야 한다. 같은 라벨이 여러 옵션에 있으면 **먼저 선언된 쪽**이 이긴다
 * (`options.find`와 같은 규칙).
 */
export interface OptionLookup {
  /** 원문 그대로의 option.value 집합 */
  byValue: Set<string>;
  /** 정규화 라벨 → option.value */
  byLabel: Map<string, string>;
}

/** 칼럼 하나의 옵션 조회표. select/radio가 아니면 undefined(조회할 것이 없다). */
export function buildOptionLookup(field: DataDefField): OptionLookup | undefined {
  if (field.widget !== 'select' && field.widget !== 'radio') return undefined;
  const byValue = new Set<string>();
  const byLabel = new Map<string, string>();
  for (const option of field.options ?? []) {
    byValue.add(option.value);
    const key = normalizeHeaderLabel(option.label);
    if (!byLabel.has(key)) byLabel.set(key, option.value);
  }
  return { byValue, byLabel };
}

/**
 * 엑셀 셀 문자열 → 칼럼 위젯에 맞는 저장 값.
 *
 * 값은 앞뒤 공백을 턴다 — 엑셀 표는 정렬용 공백을 흔히 달고 오는데, 그대로 두면
 * select 옵션 라벨 매칭이 조용히 실패하고 표시도 어긋난다(줄바꿈 등 셀 안쪽 공백은 보존).
 *
 * `lookup`은 **같은 field로 만든** 조회표여야 한다(칼럼당 한 번 만들어 셀마다 재사용).
 * 주지 않으면 여기서 만든다 — 단발 호출의 결과가 대량 변환과 달라지지 않게.
 */
export function convertCell(
  raw: string,
  field: DataDefField,
  lookup?: OptionLookup,
): CellConversion {
  const text = raw.trim();
  switch (field.widget) {
    case 'checkbox': {
      const parsed = parseCheckboxText(text);
      if (parsed === null) {
        return { value: false, warning: '체크 여부로 읽을 수 없어 해제로 두었습니다.' };
      }
      return { value: parsed };
    }
    case 'number': {
      if (text.length === 0) return { value: '' };
      const n = parseNumberText(text);
      if (n === null) {
        // 원문을 남겨 봐야 `<input type="number">`가 표시하지 못해 빈칸으로 보인다 —
        // 눈에 보이지 않는 값이 데이터에만 남는 것보다 비우고 경고하는 편이 정직하다.
        return { value: '', warning: '숫자로 읽을 수 없어 비웠습니다.' };
      }
      return { value: n };
    }
    case 'select':
    case 'radio': {
      if (text.length === 0) return { value: '' };
      const table = lookup ?? buildOptionLookup(field);
      if (table?.byValue.has(text)) return { value: text };
      const byLabel = table?.byLabel.get(normalizeHeaderLabel(text));
      if (byLabel !== undefined) return { value: byLabel };
      // 원문을 남긴다 — 비우면 무엇이 있었는지 알 수 없고, 남기면 기본행 편집에서
      // 그대로 보이고 고칠 수 있다(읽기 표시도 String(value)로 폴백된다).
      return { value: text, warning: '선택지 목록에 없는 값이라 원문 그대로 두었습니다.' };
    }
    default:
      // text — 그리고 photo(값=URL 문자열)도 여기로: 엑셀/카탈로그 가져오기는 사진을 만들 수
      // 없으므로 URL 원문을 문자열로 보존한다(비우면 무엇이 있었는지 알 수 없다 — select 규약 동형).
      return { value: text };
  }
}

/**
 * 배타 쌍 두 셀 → 한 칼럼의 저장 값.
 *
 * 마크 판정은 감지(columnsAreExclusive)와 같은 "채움 여부"다 — 감지가 배타라고 본 근거와
 * 다른 잣대로 값을 읽으면 미리보기가 감지 결과와 어긋난다.
 * - 첫 열만 마크: checkbox면 true, 아니면 첫 하위 라벨(선택 칼럼은 라벨→옵션 값 변환을 탄다)
 * - 둘째 열만 마크: checkbox면 false, 아니면 둘째 하위 라벨
 * - 양쪽 다 빔 / 양쪽 다 마크: 보정하되 행별 경고(침묵 손실 금지 — 어느 행인지 알아야 고친다)
 */
export function convertPairCell(
  firstRaw: string,
  secondRaw: string,
  field: DataDefField,
  subLabels: readonly [string, string],
  lookup?: OptionLookup,
): CellConversion {
  const firstMarked = firstRaw.trim().length > 0;
  const secondMarked = secondRaw.trim().length > 0;
  const isCheckbox = field.widget === 'checkbox';

  if (firstMarked && secondMarked) {
    const base = isCheckbox ? { value: true } : convertCell(subLabels[0], field, lookup);
    return {
      value: base.value,
      warning: `양쪽(${subLabels[0]}·${subLabels[1]})에 모두 표시가 있어 ${subLabels[0]} 쪽으로 저장했습니다.`,
    };
  }
  if (!firstMarked && !secondMarked) {
    if (isCheckbox) {
      return { value: false, warning: `양쪽(${subLabels[0]}·${subLabels[1]}) 다 비어 있어 해제로 저장했습니다.` };
    }
    return { value: '', warning: `양쪽(${subLabels[0]}·${subLabels[1]}) 다 비어 있어 비웠습니다.` };
  }
  if (isCheckbox) return { value: firstMarked };
  return convertCell(firstMarked ? subLabels[0] : subLabels[1], field, lookup);
}

// ------------------------------------------------------------
// 행 생성 (매핑 모드)
// ------------------------------------------------------------

export interface BuildRowsParams {
  fields: readonly DataDefField[];
  cells: readonly string[][];
  /** 헤더 시작 행 index(0-based). 데이터는 헤더 줄 수만큼 지난 행부터. */
  headerRowIndex: number;
  /** 헤더가 걸친 줄 수(1~3, 기본 1) — 병합 다행 헤더 대응. */
  headerRowCount?: number;
  mapping: ColumnMapping;
  /** 미매핑 칼럼에 채울 기본값(원문). 비어 있으면 빈 값. */
  defaults?: ColumnDefaults;
}

/**
 * 미매핑 칼럼 **기본값**의 변환 경고 — 행이 아니라 칼럼 단위 사실이다(모든 행에 같은 값이
 * 들어가므로 행별로 반복하면 경고 목록이 행 수만큼 불어난다).
 */
export interface DefaultValueWarning {
  fieldKey: string;
  fieldLabel: string;
  /** 사용자가 친 기본값 원문 */
  raw: string;
  message: string;
}

export interface BuildRowsResult {
  rows: Record<string, unknown>[];
  warnings: ImportWarning[];
  /** 미매핑 칼럼 기본값이 그대로 저장되지 못한 경우 — 칼럼당 1건. */
  defaultWarnings: DefaultValueWarning[];
  /** 매핑된 칸이 전부 비어 건너뛴 행 수 — 표 아래 잔재를 조용히 지우지 않았음을 알린다. */
  skippedEmptyRows: number;
}

/** 행 루프 밖에서 칼럼당 한 번 준비하는 것들 — buildColumnDefaults 반환. */
export interface ColumnDefaultsBuild {
  /** fields와 같은 순서의 옵션 조회표 — 셀 변환 루프가 index로 쓴다. */
  optionLookups: (OptionLookup | undefined)[];
  /** 미매핑 칼럼 key → 변환된 기본값 */
  defaultValues: Map<string, unknown>;
  defaultWarnings: DefaultValueWarning[];
}

/**
 * 선택지 조회표·미매핑 칼럼 기본값을 칼럼당 한 번 변환한다 — 행 루프 밖 고정비.
 *
 * 조회표를 셀마다 다시 만들면 행 수 × 옵션 수로 불어나고, 기본값은 행마다 같으므로 경고도
 * 행마다 반복하지 않는다(칼럼당 1건). 기본값도 셀과 같은 변환(convertCell)을 거치므로 같은
 * 이유로 비거나 바뀔 수 있는데, 행 경고와 달리 화면에 원본이 남지 않아(입력칸은 사용자가 친
 * 원문을 그대로 보여 준다) 알리지 않으면 무엇이 저장됐는지 확인할 길이 없다.
 *
 * mapping 값 형상은 원천마다 다르지만(엑셀 ColumnSource / 카탈로그 경로 문자열) "미매핑 =
 * null/undefined" 규약은 같아 그 판정만 본다 — 엑셀(buildRowsFromMapping)과 카탈로그
 * (buildRowsFromCatalog)가 같은 함수를 호출한다.
 */
export function buildColumnDefaults(
  fields: readonly DataDefField[],
  mapping: Record<string, unknown>,
  defaults: ColumnDefaults,
): ColumnDefaultsBuild {
  const optionLookups = fields.map((field) => buildOptionLookup(field));
  const defaultValues = new Map<string, unknown>();
  const defaultWarnings: DefaultValueWarning[] = [];
  fields.forEach((field, fi) => {
    if (mapping[field.key] !== null && mapping[field.key] !== undefined) return;
    const rawDefault = defaults[field.key] ?? '';
    const { value, warning } = convertCell(rawDefault, field, optionLookups[fi]);
    defaultValues.set(field.key, value);
    if (warning) {
      defaultWarnings.push({
        fieldKey: field.key,
        fieldLabel: field.label || field.key,
        raw: rawDefault,
        message: warning,
      });
    }
  });
  return { optionLookups, defaultValues, defaultWarnings };
}

/**
 * 매핑대로 데이터 행을 만든다.
 *
 * - 매핑된 칸이 **전부 빈** 행은 건너뛴다(엑셀 표 아래 서식만 남은 빈 줄). 미매핑 칼럼의
 *   기본값만으로 행을 만들면 내용 없는 행이 무한정 생긴다.
 * - 칼럼은 불변이므로 결과 행은 항상 `fields`의 키를 모두 갖는다(빠진 키가 있으면 렌더가
 *   빈칸으로 떨어지고 이후 편집에서 키가 어긋난다).
 */
export function buildRowsFromMapping({
  fields,
  cells,
  headerRowIndex,
  headerRowCount = 1,
  mapping,
  defaults = {},
}: BuildRowsParams): BuildRowsResult {
  const rows: Record<string, unknown>[] = [];
  const warnings: ImportWarning[] = [];
  let skippedEmptyRows = 0;

  const { optionLookups, defaultValues, defaultWarnings } = buildColumnDefaults(
    fields,
    mapping,
    defaults,
  );

  for (let r = headerRowIndex + Math.max(1, headerRowCount); r < cells.length; r += 1) {
    const sourceRow = cells[r] ?? [];
    // 칼럼별 원문 — 쌍 매핑은 두 셀을 튜플로 든다(빈 행 판정·변환·경고 원문이 모두 두 셀 기준).
    const mappedTexts = fields.map((f): string | [string, string] => {
      const source = mapping[f.key];
      if (source === null || source === undefined) return '';
      if (isPairRef(source)) {
        return [
          (sourceRow[source.pair[0]] ?? '').trim(),
          (sourceRow[source.pair[1]] ?? '').trim(),
        ];
      }
      return (sourceRow[source] ?? '').trim();
    });
    const rowIsEmpty = mappedTexts.every((t) =>
      typeof t === 'string' ? t.length === 0 : t.every((cell) => cell.length === 0),
    );
    if (rowIsEmpty) {
      skippedEmptyRows += 1;
      continue;
    }

    const row: Record<string, unknown> = {};
    fields.forEach((field, fi) => {
      const source = mapping[field.key];
      if (source === null || source === undefined) {
        row[field.key] = defaultValues.get(field.key) ?? '';
        return;
      }
      const texts = mappedTexts[fi];
      let raw: string;
      let conversion: CellConversion;
      if (isPairRef(source)) {
        const [firstRaw, secondRaw] = texts as [string, string];
        conversion = convertPairCell(firstRaw, secondRaw, field, source.subLabels, optionLookups[fi]);
        // 경고 원문은 채워진 쪽만 잇는다 — 양쪽 다 비면 ''라 화면의 '(빈 값)' 표기로 떨어진다.
        raw = [firstRaw, secondRaw].filter((t) => t.length > 0).join(' / ');
      } else {
        raw = texts as string;
        conversion = convertCell(raw, field, optionLookups[fi]);
      }
      row[field.key] = conversion.value;
      if (conversion.warning) {
        warnings.push({
          rowNumber: r + 1,
          fieldKey: field.key,
          fieldLabel: field.label || field.key,
          raw,
          message: conversion.warning,
        });
      }
    });
    rows.push(row);
  }

  return { rows, warnings, defaultWarnings, skippedEmptyRows };
}

// ------------------------------------------------------------
// 칼럼 구성 (구성 모드 — 신규 데이터셋)
// ------------------------------------------------------------

/** 위젯 추론이 다루는 범위 — 나머지(textarea/select/radio)는 사람이 고르는 축이다. */
export type InferredWidget = 'text' | 'number' | 'checkbox';

/**
 * 한 엑셀 칼럼의 값들로 셀 위젯을 추론한다(제안일 뿐 — 사용자가 확인·수정한다).
 *
 * 0/1만 있는 칼럼은 **number**로 본다: 체크 표기로 읽으려면 O·예·TRUE처럼 숫자가 아닌
 * 근거가 한 번은 나와야 한다. 그렇지 않으면 수량 칼럼이 전부 체크박스로 둔갑한다.
 */
export function inferFieldWidget(values: readonly string[]): InferredWidget {
  const filled = values.map((v) => v.trim()).filter((v) => v.length > 0);
  if (filled.length === 0) return 'text';

  const allBooleanish = filled.every((v) => parseCheckboxText(v) !== null);
  const hasNonNumericBoolean = filled.some((v) => parseNumberText(v) === null);
  if (allBooleanish && hasNonNumericBoolean) return 'checkbox';

  if (filled.every((v) => parseNumberText(v) !== null)) return 'number';
  return 'text';
}

/**
 * 배타 쌍의 첫 열 draft에 붙는 병합 정보 — merged가 켜져 있으면(기본) 이 draft 하나가
 * 두 열을 대표하고, 둘째 열 draft는 목록·칼럼 정의·매핑에서 빠진다(mergedPairSecondCols).
 */
export interface ComposePairInfo {
  /** 쌍의 둘째 엑셀 열 index */
  secondColIndex: number;
  /** [첫 열, 둘째 열] 하위 헤더 라벨(예: ['양호', '불량']) */
  subLabels: [string, string];
  /** true(기본)=1칼럼으로 합침 / false=기존처럼 개별 2칼럼 — 사용자가 결정한다. */
  merged: boolean;
  /** 병합 시 라벨(상위 헤더 — '점검결과'). 토글이 label을 이 값과 firstSoloLabel 사이에서 바꾼다. */
  mergedLabel: string;
  /** 병합 해제 시 첫 열이 되찾을 개별 라벨(합성 헤더 — '점검결과 양호') */
  firstSoloLabel: string;
  /** 병합 해제 시 첫 열 단독 값으로 추론한 위젯 */
  firstSoloWidget: InferredWidget;
}

/** 구성 모드의 칼럼 후보 한 줄 — 사용자가 체크·라벨·위젯을 손볼 수 있는 초안. */
export interface ComposeColumnDraft {
  /** 엑셀 칼럼 index */
  colIndex: number;
  /** 가져올지 여부(기본: 헤더 문구가 있으면 true) */
  include: boolean;
  /** 칼럼 표시명 — 헤더 문구(비면 `칼럼 N`) */
  label: string;
  /** 발급된 칼럼 key */
  key: string;
  widget: InferredWidget;
  /** 배타 쌍의 첫 열이면 병합 정보가 붙는다. */
  pair?: ComposePairInfo;
}

/**
 * 헤더 행 + 표본 값에서 칼럼 초안을 만든다.
 *
 * key는 신규 데이터셋 생성 직전에 발급하므로 참조 파급이 0이다(＋칼럼과 같은 `field{N}`
 * 관례 — 발급 규칙은 호출측 nextKey에 위임해 EditorPage의 정본 함수를 그대로 쓴다).
 * 헤더 문구가 빈 칼럼은 기본 미선택 — 서식용 여백 열이 그대로 칼럼이 되는 것을 막는다.
 * 라벨은 합성 헤더(buildHeaderLabels) — 다행 헤더면 위아래 문구가 붙는다.
 */
export function buildComposeColumnDrafts(params: {
  cells: readonly string[][];
  headerRowIndex: number;
  /** 헤더가 걸친 줄 수(1~3, 기본 1) — 표본 값도 그만큼 지난 행부터 읽는다. */
  headerRowCount?: number;
  /** 이미 발급한 키 목록을 받아 다음 키를 돌려주는 함수(EditorPage nextAutoFieldKey 주입) */
  nextKey: (existingKeys: readonly string[]) => string;
}): ComposeColumnDraft[] {
  const { cells, headerRowIndex, headerRowCount = 1, nextKey } = params;
  const labels = buildHeaderLabels(cells, headerRowIndex, headerRowCount);
  const colCount = cells.reduce((max, row) => Math.max(max, row.length), labels.length);
  const dataStartRow = headerRowIndex + Math.max(1, headerRowCount);
  // 배타 쌍은 기본 병합으로 제안한다 — 데이터 가치로는 "점검결과" 1칼럼(양호=true)이 맞고,
  // 개별 2칼럼을 원하면 draft의 토글로 푼다. 둘째 열도 draft·key는 그대로 만들어 둔다
  // (해제 시 제자리에 나타나야 하고, 안 쓰인 key의 번호 공백은 무해하다).
  const pairByFirstCol = new Map(
    detectExclusivePairs(cells, headerRowIndex, headerRowCount).map((p) => [p.colIndexes[0], p]),
  );
  const drafts: ComposeColumnDraft[] = [];
  const issued: string[] = [];

  for (let c = 0; c < colCount; c += 1) {
    const headerText = (labels[c] ?? '').trim();
    const sample: string[] = [];
    for (let r = dataStartRow; r < cells.length; r += 1) sample.push(cells[r]?.[c] ?? '');
    const key = nextKey(issued);
    issued.push(key);
    const soloLabel = headerText || `칼럼 ${c + 1}`;
    const soloWidget = inferFieldWidget(sample);
    const pairCandidate = pairByFirstCol.get(c);
    drafts.push({
      colIndex: c,
      include: headerText.length > 0,
      label: pairCandidate ? pairCandidate.topLabel : soloLabel,
      key,
      widget: pairCandidate ? 'checkbox' : soloWidget,
      ...(pairCandidate
        ? {
            pair: {
              secondColIndex: pairCandidate.colIndexes[1],
              subLabels: pairCandidate.subLabels,
              merged: true,
              mergedLabel: pairCandidate.topLabel,
              firstSoloLabel: soloLabel,
              firstSoloWidget: soloWidget,
            },
          }
        : {}),
    });
  }
  return drafts;
}

/** 병합 중인 쌍의 둘째 열 집합 — 이 열의 draft는 목록 표시·칼럼 정의·매핑에서 빠진다. */
export function mergedPairSecondCols(drafts: readonly ComposeColumnDraft[]): Set<number> {
  const hidden = new Set<number>();
  for (const d of drafts) if (d.pair?.merged) hidden.add(d.pair.secondColIndex);
  return hidden;
}

/**
 * 쌍 draft의 병합 토글 — 라벨·위젯도 그 모드의 기본값으로 되돌린다.
 *
 * 사용자가 고친 라벨을 보존하지 않는 것은 의도다: 병합 라벨('점검결과')과 개별 라벨
 * ('점검결과 양호')은 서로 다른 것의 이름이라, 한쪽에서 고친 문구를 다른 쪽에 끌고 가면
 * 뜻이 어긋난 이름이 조용히 남는다.
 */
export function togglePairMerge(
  drafts: readonly ComposeColumnDraft[],
  draftIndex: number,
): ComposeColumnDraft[] {
  return drafts.map((d, i) => {
    if (i !== draftIndex || !d.pair) return d;
    const merged = !d.pair.merged;
    return {
      ...d,
      label: merged ? d.pair.mergedLabel : d.pair.firstSoloLabel,
      widget: merged ? ('checkbox' as const) : d.pair.firstSoloWidget,
      pair: { ...d.pair, merged },
    };
  });
}

/** 구성 모드 초안 → 실제 칼럼 정의(선택된 것만, 표시 순서 유지. 병합 쌍의 둘째 열은 제외). */
export function draftsToFields(drafts: readonly ComposeColumnDraft[]): DataDefField[] {
  const hidden = mergedPairSecondCols(drafts);
  return drafts
    .filter((d) => d.include && !hidden.has(d.colIndex))
    .map((d) => ({
      key: d.key,
      label: d.label,
      // 'text'는 기본값이라 굳이 저장하지 않는다(＋칼럼이 만드는 칼럼과 같은 형상).
      ...(d.widget === 'text' ? {} : { widget: d.widget }),
    }));
}

/** 구성 모드 초안 → 매핑(선택된 칼럼만 연결. 병합 쌍 draft는 두 열을 쌍 원천으로 연결). */
export function draftsToMapping(drafts: readonly ComposeColumnDraft[]): ColumnMapping {
  const hidden = mergedPairSecondCols(drafts);
  const mapping: ColumnMapping = {};
  for (const d of drafts) {
    if (!d.include || hidden.has(d.colIndex)) continue;
    mapping[d.key] = d.pair?.merged
      ? { pair: [d.colIndex, d.pair.secondColIndex], subLabels: d.pair.subLabels }
      : d.colIndex;
  }
  return mapping;
}
